import { Suspense, lazy, useEffect, useState, type CSSProperties, type InputHTMLAttributes, type PropsWithChildren, type ReactNode, type SVGProps } from 'react';
import successIcon from '@/assets/images/success-icon.svg';
import failIcon from '@/assets/images/fail-icon.svg';
import { useI18n } from '@/lib/i18n';

type LottieModule = { default: typeof import('lottie-react').default };
type AnimationDataModule = { default: object };

const LazyLottie = lazy(async (): Promise<LottieModule> => {
  const module = await import('lottie-react');
  return { default: module.default };
});

const loadLoadingAnimation = () => import('@/assets/lottie/loading.json');
const loadEmptyAnimation = () => import('@/assets/lottie/Empty.json');

function LottieFallback({ size }: { size: number }) {
  return (
    <div
      className="skeleton"
      aria-hidden="true"
      style={{ width: size, height: size, borderRadius: '50%' }}
    />
  );
}

function DeferredLottie({
  size,
  loader,
  loop = true,
}: {
  size: number;
  loader: () => Promise<AnimationDataModule>;
  loop?: boolean;
}) {
  const [animationData, setAnimationData] = useState<object | null>(null);

  useEffect(() => {
    let active = true;

    void loader().then((module) => {
      if (active) setAnimationData(module.default);
    });

    return () => {
      active = false;
    };
  }, [loader]);

  if (!animationData) {
    return <LottieFallback size={size} />;
  }

  return (
    <Suspense fallback={<LottieFallback size={size} />}>
      <LazyLottie animationData={animationData} loop={loop} style={{ width: size, height: size }} />
    </Suspense>
  );
}

// ===================== Toast System =====================

type ToastTone = 'success' | 'error' | 'info' | 'warning';

type ToastItem = {
  id: number;
  message: string;
  tone: ToastTone;
  title?: string;
};

let toastId = 0;
let activeToasts: ToastItem[] = [];
const toastListeners = new Set<(toasts: ToastItem[]) => void>();

function emitToasts() {
  for (const listener of toastListeners) listener(activeToasts);
}

function dismissToast(id: number) {
  activeToasts = activeToasts.filter((toast) => toast.id !== id);
  emitToasts();
}

export function showToast(message: string, tone: ToastTone = 'info', title?: string) {
  const id = ++toastId;
  activeToasts = [...activeToasts, { id, message, tone, title }];
  emitToasts();
  window.setTimeout(() => dismissToast(id), 4000);
}

const toastIcons: Record<ToastTone, string> = {
  success: 'S',
  error: 'X',
  info: 'i',
  warning: '!',
};

export function ToastViewport() {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<ToastItem[]>(activeToasts);

  useEffect(() => {
    toastListeners.add(setToasts);
    return () => {
      toastListeners.delete(setToasts);
    };
  }, []);

  return (
    <div className="toast-viewport" aria-live="polite" aria-atomic="true">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.tone}`} role="status">
          <div className="toast-icon" aria-hidden="true">
            {toastIcons[toast.tone]}
          </div>
          <div className="toast-content">
            <div className="toast-title">
              {toast.title ||
                {
                  success: t.common.success,
                  error: t.common.failed,
                  info: t.common.info,
                  warning: t.common.warning,
                }[toast.tone]}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
          <button className="toast-close" onClick={() => dismissToast(toast.id)} aria-label={t.common.dismiss}>
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ===================== Layout Components =====================

export function AtomBackground({ children }: PropsWithChildren) {
  return <div className="page">{children}</div>;
}

interface FrostedPanelProps {
  className?: string;
  hover?: boolean;
  style?: React.CSSProperties;
}

export function FrostedPanel({ children, className, hover = false, style }: PropsWithChildren<FrostedPanelProps>) {
  return <div className={`card ${hover ? 'card-hover' : ''} ${className ?? ''}`.trim()} style={style}>{children}</div>;
}

export function GlassCard({ children, className, padding = 'normal' }: PropsWithChildren<{ className?: string; padding?: 'none' | 'small' | 'normal' | 'large' }>) {
  const paddingClass = padding === 'none' ? '' : padding === 'small' ? 'p-3' : padding === 'large' ? 'p-6' : 'p-4';
  return <div className={`glass-card ${paddingClass} ${className ?? ''}`.trim()}>{children}</div>;
}

// ===================== Loading Components =====================

export function LoadingView({ size = 120, text }: { size?: number; text?: string }) {
  const { t } = useI18n();
  return (
    <div className="loading-view">
      <DeferredLottie size={size} loader={loadLoadingAnimation} />
      {(text ?? t.common.loading) && <p className="loading-text">{text ?? t.common.loading}</p>}
    </div>
  );
}

export function LoadingInline({ size = 24 }: { size?: number }) {
  return <DeferredLottie size={size} loader={loadLoadingAnimation} />;
}

export function LoadingOverlay({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="loading-overlay">
      <LoadingView size={100} text="" />
    </div>
  );
}

export function LoadingDots() {
  return (
    <span className="loading-dots">
      <span></span>
      <span></span>
      <span></span>
    </span>
  );
}

// ===================== Skeleton/Shimmer =====================

export function Skeleton({ width, height, circle = false, className, style: customStyle }: { width?: string | number; height?: string | number; circle?: boolean; className?: string; style?: React.CSSProperties }) {
  const style: React.CSSProperties = {
    width: width ?? '100%',
    height: height ?? '1em',
    borderRadius: circle ? '50%' : '8px',
    ...customStyle,
  };
  return <div className={`skeleton ${className ?? ''}`.trim()} style={style} />;
}

export function SkeletonText({ lines = 1 }: { lines?: number }) {
  return (
    <div className="skeleton-text">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height="0.875em" width={i === lines - 1 && lines > 1 ? '75%' : '100%'} />
      ))}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div className="skeleton-card skeleton-card-grid">
      <Skeleton circle width={64} height={64} style={{ margin: '0 auto' }} />
      <Skeleton height="1em" width="80%" style={{ margin: '12px auto 0' }} />
    </div>
  );
}

// ===================== Empty & Error States =====================

export function EmptyView({ message, action }: { message: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div className="empty-view">
      <DeferredLottie size={160} loader={loadEmptyAnimation} />
      <p className="empty-message">{message}</p>
      {action && (
        <button className="secondary-btn empty-action" onClick={action.onClick}>
          <span>{action.label}</span>
          <Icon name="arrow-right" size={16} strokeWidth={2.25} />
        </button>
      )}
    </div>
  );
}

export function ErrorView({ message, onRetry, title, children }: { message: string; onRetry?: () => void; title?: string; children?: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="result-view">
      <img src={failIcon} alt="Failed" className="result-icon" />
      <h3 className="result-title" style={{ color: 'var(--error)' }}>{title ?? t.common.somethingWentWrong}</h3>
      <p className="result-message">{message}</p>
      {children && <div className="result-details">{children}</div>}
      {onRetry && (
        <button className="primary-btn result-btn" onClick={onRetry}>
          {t.common.retry}
        </button>
      )}
    </div>
  );
}

export function SuccessView({ title, message, children }: PropsWithChildren<{ title?: string; message: string }>) {
  return (
    <div className="result-view">
      <img src={successIcon} alt="Success" className="result-icon" />
      <h3 className="result-title" style={{ color: 'var(--success)' }}>{title ?? 'Success'}</h3>
      <p className="result-message">{message}</p>
      {children && <div className="result-details">{children}</div>}
    </div>
  );
}

// ===================== Form Components =====================

function resolveInputIcon(icon: ReactNode): ReactNode {
  if (typeof icon !== 'string') return icon;

  switch (icon.trim().toUpperCase()) {
    case 'TEL':
      return <Icon name="phone" size={18} />;
    case 'MAIL':
      return <Icon name="mail" size={18} />;
    case 'KEY':
    case 'LOCK':
      return <Icon name="lock" size={18} />;
    case 'USER':
      return <Icon name="user" size={18} />;
    case 'ID':
      return <Icon name="badge" size={18} />;
    case 'SV':
      return <Icon name="server" size={18} />;
    case 'IN':
      return <Icon name="hash" size={18} />;
    case 'ORD':
      return <Icon name="receipt" size={18} />;
    case 'PAY':
      return <Icon name="wallet" size={18} />;
    case 'R':
      return <Icon name="refresh" size={18} />;
    default:
      return icon;
  }
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper?: string;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
}

export function Input({ label, error, helper, icon, iconRight, loading, className, disabled, ...props }: InputProps) {
  return (
    <div className={`input-wrapper ${className ?? ''}`.trim()}>
      {label && <label className="input-label">{label}</label>}
      <div className={`input-container ${error ? 'input-error' : ''} ${disabled ? 'input-disabled' : ''}`.trim()}>
        {icon && <span className="input-icon input-icon-left">{resolveInputIcon(icon)}</span>}
        <input className="input-field" disabled={disabled || loading} {...props} />
        {loading && <LoadingInline size={18} />}
        {iconRight && !loading && <span className="input-icon input-icon-right">{resolveInputIcon(iconRight)}</span>}
      </div>
      {error && <span className="input-error-text">{error}</span>}
      {helper && !error && <span className="input-helper">{helper}</span>}
    </div>
  );
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({ children, variant = 'primary', size = 'md', loading, fullWidth, disabled, className, ...props }: PropsWithChildren<ButtonProps>) {
  const baseClass = `btn btn-${variant} btn-${size}`;
  const widthClass = fullWidth ? 'btn-full' : '';
  return (
    <button className={`${baseClass} ${widthClass} ${className ?? ''}`.trim()} disabled={disabled || loading} {...props}>
      {loading ? <LoadingInline size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} /> : children}
    </button>
  );
}

// ===================== Display Components =====================

type IconName =
  | 'menu'
  | 'bolt'
  | 'check'
  | 'alert'
  | 'phone'
  | 'mail'
  | 'lock'
  | 'user'
  | 'badge'
  | 'server'
  | 'hash'
  | 'receipt'
  | 'wallet'
  | 'refresh'
  | 'arrow-right'
  | 'arrow-left'
  | 'close'
  | 'upload'
  | 'credit-card'
  | 'log-out'
  | 'search'
  | 'bell'
  | 'gift'
  | 'diamond'
  | 'smartphone'
  | 'tablet'
  | 'monitor'
  | 'cpu'
  | 'chat'
  | 'shield'
  | 'headset';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  strokeWidth?: number;
}

export function Icon({ name, size = 18, strokeWidth = 2, style, ...props }: IconProps) {
  const baseProps: SVGProps<SVGSVGElement> = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    style: { display: 'block', flexShrink: 0, ...(style as CSSProperties | undefined) },
    ...props,
  };

  switch (name) {
    case 'menu':
      return (
        <svg {...baseProps}>
          <path d="M4 7h16" />
          <path d="M4 12h16" />
          <path d="M4 17h16" />
        </svg>
      );
    case 'bolt':
      return (
        <svg {...baseProps}>
          <path d="M13 2 6 13h5l-1 9 8-12h-5l0-8Z" />
        </svg>
      );
    case 'check':
      return (
        <svg {...baseProps}>
          <path d="m5 12 5 5L20 7" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...baseProps}>
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        </svg>
      );
    case 'phone':
      return (
        <svg {...baseProps}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.34 1.77.66 2.6a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.48-1.23a2 2 0 0 1 2.11-.45c.83.32 1.7.54 2.6.66A2 2 0 0 1 22 16.92Z" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...baseProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m4 7 8 6 8-6" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...baseProps}>
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
          <path d="M12 15v2" />
        </svg>
      );
    case 'user':
      return (
        <svg {...baseProps}>
          <path d="M20 21a8 8 0 1 0-16 0" />
          <circle cx="12" cy="8" r="4" />
        </svg>
      );
    case 'badge':
      return (
        <svg {...baseProps}>
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8 9h8" />
          <path d="M8 13h5" />
        </svg>
      );
    case 'server':
      return (
        <svg {...baseProps}>
          <rect x="3" y="4" width="18" height="6" rx="2" />
          <rect x="3" y="14" width="18" height="6" rx="2" />
          <path d="M7 7h.01" />
          <path d="M7 17h.01" />
          <path d="M11 7h6" />
          <path d="M11 17h6" />
        </svg>
      );
    case 'hash':
      return (
        <svg {...baseProps}>
          <path d="M5 9h14" />
          <path d="M5 15h14" />
          <path d="M9 4 7 20" />
          <path d="m17 4-2 16" />
        </svg>
      );
    case 'receipt':
      return (
        <svg {...baseProps}>
          <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" />
          <path d="M9 8h6" />
          <path d="M9 12h6" />
        </svg>
      );
    case 'wallet':
      return (
        <svg {...baseProps}>
          <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H5a2 2 0 0 0-2 2V7Z" />
          <path d="M3 10a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7Z" />
          <path d="M17 14h.01" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...baseProps}>
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
      );
    case 'arrow-right':
      return (
        <svg {...baseProps}>
          <path d="M5 12h14" />
          <path d="m13 5 7 7-7 7" />
        </svg>
      );
    case 'arrow-left':
      return (
        <svg {...baseProps}>
          <path d="M19 12H5" />
          <path d="m11 5-7 7 7 7" />
        </svg>
      );
    case 'close':
      return (
        <svg {...baseProps}>
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...baseProps}>
          <path d="M12 16V4" />
          <path d="m7 9 5-5 5 5" />
          <path d="M4 20h16" />
        </svg>
      );
    case 'credit-card':
      return (
        <svg {...baseProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18" />
          <path d="M7 15h4" />
        </svg>
      );
    case 'log-out':
      return (
        <svg {...baseProps}>
          <path d="M14 4h5v16h-5" />
          <path d="M10 12h9" />
          <path d="m12 9-3 3 3 3" />
          <path d="M5 4h5" />
          <path d="M5 20h5" />
        </svg>
      );
    case 'search':
      return (
        <svg {...baseProps}>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...baseProps}>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
      );
    case 'gift':
      return (
        <svg {...baseProps}>
          <rect x="3" y="8" width="18" height="4" rx="1" />
          <path d="M12 8v13" />
          <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
          <path d="M7.5 8a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 2.5 2.5v5" />
          <path d="M16.5 8v-2.5a2.5 2.5 0 0 1 5 0 2.5 2.5 0 0 1-2.5 2.5h-5" />
        </svg>
      );
    case 'diamond':
      return (
        <svg {...baseProps}>
          <path d="M12 3 4 10l8 11 8-11-8-7Z" />
          <path d="M8 3h8" />
          <path d="m4 10 16 0" />
          <path d="m9 10 3 11 3-11" />
          <path d="m8 3 4 7 4-7" />
        </svg>
      );
    case 'smartphone':
      return (
        <svg {...baseProps}>
          <rect x="6" y="2" width="12" height="20" rx="2" />
          <path d="M12 18h.01" />
        </svg>
      );
    case 'tablet':
      return (
        <svg {...baseProps}>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <path d="M12 18h.01" />
        </svg>
      );
    case 'monitor':
      return (
        <svg {...baseProps}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8" />
          <path d="M12 17v4" />
        </svg>
      );
    case 'cpu':
      return (
        <svg {...baseProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M9 9h6v6H9z" />
          <path d="M9 1v3" />
          <path d="M15 1v3" />
          <path d="M9 20v3" />
          <path d="M15 20v3" />
          <path d="M20 9h3" />
          <path d="M20 15h3" />
          <path d="M1 9h3" />
          <path d="M1 15h3" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...baseProps}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'headset':
      return (
        <svg {...baseProps}>
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
        </svg>
      );
    default:
      return null;
  }
}

export function BalanceChip({ coins, className }: { coins?: number | null; className?: string }) {
  const formatted = (coins ?? 0).toLocaleString();
  return (
    <span className={`balance-chip ${className ?? ''}`.trim()}>
      <span className="balance-chip-icon">$</span>
      <span className="balance-chip-value">{formatted}</span>
      <span className="balance-chip-currency">MMK</span>
    </span>
  );
}

type BadgeTone = 'default' | 'success' | 'error' | 'warning' | 'info';

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  dot?: boolean;
  pulse?: boolean;
}

export function Badge({ children, tone = 'default', dot, pulse }: BadgeProps) {
  return (
    <span className={`badge badge-${tone} ${pulse ? 'badge-pulse' : ''}`.trim()}>
      {dot && <span className="badge-dot" />}
      {children}
    </span>
  );
}

export function StatusPill({ status, children }: { status: 'success' | 'error' | 'pending' | 'processing'; children: ReactNode }) {
  const icons = { success: 'OK', error: 'ERR', pending: '...', processing: '<>' };
  return (
    <span className={`status-pill status-${status}`.trim()}>
      <span className="status-pill-icon">{icons[status]}</span>
      {children}
    </span>
  );
}

// ===================== Modal/Dialog =====================

export function Modal({ open, onClose, title, children }: PropsWithChildren<{ open: boolean; onClose: () => void; title?: string }>) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="modal-header">
            <h3>{title}</h3>
            <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
          </div>
        )}
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ===================== Divider =====================

export function Divider({ text }: { text?: string }) {
  if (text) {
    return (
      <div className="divider divider-with-text">
        <span className="divider-line" />
        <span className="divider-text">{text}</span>
        <span className="divider-line" />
      </div>
    );
  }
  return <div className="divider" />;
}

// Re-export OptimizedImage for convenience
export { OptimizedImage } from './OptimizedImage';
